import { supabase } from '@/integrations/supabase/client';
import type { Json, Tables } from '@/integrations/supabase/types';
import {
  createDiaryMemoryTitle,
  inferDiaryMemoryFactKind,
  inferDiaryMemoryType,
} from '@/lib/diary';
import {
  DIARY_MEMORY_FACT_KINDS,
  DIARY_MEMORY_SOURCES,
  DIARY_MEMORY_STATUSES,
  DIARY_MEMORY_TYPES,
  type DiaryContext,
  type DiaryContextSummary,
  type DiaryMemoryFactKind,
  type DiaryMemory,
  type DiaryMemoryDraft,
  type DiaryMemorySource,
  type DiaryMemorySuggestion,
  type DiaryMemorySuggestionRequest,
  type DiaryMemoryStatus,
  type DiaryMemoryType,
} from '@/types/diary';

type DiaryMemoryRow = Tables<'diary_memories'>;

const DEFAULT_FETCH_LIMIT = 24;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function toOptionalString(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0 ? value : null;
}

function toNumber(value: unknown, fallback = 0) {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function clampConfidence(value: number) {
  return Math.max(0, Math.min(1, value));
}

function toNonEmptyString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function normalizeDiaryMemoryType(value: string | null | undefined): DiaryMemoryType {
  return DIARY_MEMORY_TYPES.includes(value as DiaryMemoryType)
    ? (value as DiaryMemoryType)
    : 'life_context';
}

function normalizeDiaryMemorySource(value: string | null | undefined): DiaryMemorySource {
  return DIARY_MEMORY_SOURCES.includes(value as DiaryMemorySource)
    ? (value as DiaryMemorySource)
    : 'manual';
}

function normalizeDiaryMemoryStatus(value: string | null | undefined): DiaryMemoryStatus {
  return DIARY_MEMORY_STATUSES.includes(value as DiaryMemoryStatus)
    ? (value as DiaryMemoryStatus)
    : 'active';
}

function normalizeDiaryMemoryFactKind(value: unknown): DiaryMemoryFactKind | null {
  return DIARY_MEMORY_FACT_KINDS.includes(value as DiaryMemoryFactKind)
    ? (value as DiaryMemoryFactKind)
    : null;
}

function sanitizeMetadata(
  metadata: Record<string, unknown> | undefined,
  factKind: DiaryMemoryFactKind | null,
) {
  const sanitized = JSON.parse(JSON.stringify(metadata ?? {})) as Record<string, unknown>;

  if (factKind) {
    sanitized.fact_kind = factKind;
  } else {
    delete sanitized.fact_kind;
  }

  return sanitized;
}

function mapDiaryMemory(row: DiaryMemoryRow): DiaryMemory {
  return {
    id: row.id,
    title: row.title,
    body: row.body,
    memory_type: normalizeDiaryMemoryType(row.memory_type),
    source: normalizeDiaryMemorySource(row.source),
    confidence: clampConfidence(toNumber(row.confidence, 1)),
    user_confirmed: row.user_confirmed,
    is_pinned: row.is_pinned,
    status: normalizeDiaryMemoryStatus(row.status),
    expires_at: row.expires_at,
    metadata: isRecord(row.metadata) ? (row.metadata as Record<string, unknown>) : {},
    origin_chat_session_id: row.origin_chat_session_id,
    origin_chat_message_id: row.origin_chat_message_id,
    origin_vault_document_id: row.origin_vault_document_id,
    created_at: row.created_at,
    updated_at: row.updated_at,
  };
}

function normalizeDiaryDraft(draft: DiaryMemoryDraft) {
  const body = draft.body.trim();
  if (!body) {
    throw new Error('Diary memory text is required.');
  }

  const factKind =
    normalizeDiaryMemoryFactKind(draft.metadata?.fact_kind) ?? inferDiaryMemoryFactKind(body);

  return {
    title: draft.title?.trim() || createDiaryMemoryTitle(body),
    body,
    memory_type: draft.memory_type ?? inferDiaryMemoryType(body),
    source: draft.source ?? 'manual',
    confidence: clampConfidence(draft.confidence ?? 1),
    user_confirmed: draft.user_confirmed ?? true,
    is_pinned: draft.is_pinned ?? false,
    status: draft.status ?? 'active',
    expires_at: draft.expires_at ?? null,
    metadata: sanitizeMetadata(draft.metadata, factKind),
    origin_chat_session_id: draft.origin_chat_session_id ?? null,
    origin_chat_message_id: draft.origin_chat_message_id ?? null,
    origin_vault_document_id: draft.origin_vault_document_id ?? null,
  };
}

function toDiaryPayload(draft: DiaryMemoryDraft): Json {
  return normalizeDiaryDraft(draft) as unknown as Json;
}

function toDiaryPayloadRecord(draft: DiaryMemoryDraft): Record<string, Json | undefined> {
  return normalizeDiaryDraft(draft) as unknown as Record<string, Json | undefined>;
}

function isDiaryMemoryExpired(expiresAt: string | null) {
  if (!expiresAt) return false;
  const timestamp = new Date(expiresAt).getTime();
  return Number.isFinite(timestamp) && timestamp <= Date.now();
}

function toDiaryContextSummary(value: unknown): DiaryContextSummary {
  if (!isRecord(value)) {
    return {
      total_active: 0,
      pinned: 0,
      constraints: 0,
      last_updated_at: null,
    };
  }

  return {
    total_active: toNumber(value.total_active),
    pinned: toNumber(value.pinned),
    constraints: toNumber(value.constraints),
    last_updated_at: toOptionalString(value.last_updated_at),
  };
}

function toDiaryContextMemory(value: unknown): DiaryMemory | null {
  if (!isRecord(value)) return null;

  const body = toOptionalString(value.body) ?? '';
  if (!body) return null;

  return {
    id: toOptionalString(value.id) ?? crypto.randomUUID(),
    title: toOptionalString(value.title) ?? createDiaryMemoryTitle(body),
    body,
    memory_type: normalizeDiaryMemoryType(toOptionalString(value.memory_type)),
    source: normalizeDiaryMemorySource(toOptionalString(value.source)),
    confidence: clampConfidence(toNumber(value.confidence, 1)),
    user_confirmed: value.user_confirmed !== false,
    is_pinned: value.is_pinned === true,
    status: normalizeDiaryMemoryStatus(toOptionalString(value.status)),
    expires_at: toOptionalString(value.expires_at),
    metadata: isRecord(value.metadata) ? value.metadata : {},
    origin_chat_session_id: toOptionalString(value.origin_chat_session_id),
    origin_chat_message_id: toOptionalString(value.origin_chat_message_id),
    origin_vault_document_id: toOptionalString(value.origin_vault_document_id),
    created_at: toOptionalString(value.created_at) ?? new Date(0).toISOString(),
    updated_at: toOptionalString(value.updated_at) ?? new Date(0).toISOString(),
  };
}

function normalizeDiaryContext(value: unknown): DiaryContext {
  if (!isRecord(value)) {
    return {
      summary: toDiaryContextSummary(null),
      identity_facts: [],
      active_constraints: [],
      recent_memories: [],
    };
  }

  const identityFacts = Array.isArray(value.identity_facts)
    ? value.identity_facts
        .map(toDiaryContextMemory)
        .filter((memory): memory is DiaryMemory => Boolean(memory))
    : [];

  const activeConstraints = Array.isArray(value.active_constraints)
    ? value.active_constraints
        .map(toDiaryContextMemory)
        .filter((memory): memory is DiaryMemory => Boolean(memory))
    : [];

  const recentMemories = Array.isArray(value.recent_memories)
    ? value.recent_memories
        .map(toDiaryContextMemory)
        .filter((memory): memory is DiaryMemory => Boolean(memory))
    : [];

  return {
    summary: toDiaryContextSummary(value.summary),
    identity_facts: identityFacts,
    active_constraints: activeConstraints,
    recent_memories: recentMemories,
  };
}

function toDiaryError(error: unknown) {
  const maybeError = error as { code?: string; message?: string };
  if (maybeError?.code === '23505') {
    return new Error('This chat message is already saved in your diary.');
  }

  return error instanceof Error ? error : new Error(String(error));
}

function normalizeDiaryMemorySuggestion(value: unknown): DiaryMemorySuggestion {
  if (!isRecord(value)) {
    throw new Error('The memory suggestion returned an invalid response.');
  }

  const shouldSave = value.should_save === true;
  const reason = toOptionalString(value.reason) ?? undefined;

  if (!shouldSave) {
    return {
      should_save: false,
      reason,
    };
  }

  const body = toNonEmptyString(value.body);
  if (!body) {
    return {
      should_save: false,
      reason: reason ?? 'No durable memory was found in this turn.',
    };
  }

  const title = toNonEmptyString(value.title) || createDiaryMemoryTitle(body);

  return {
    should_save: true,
    title,
    body,
    memory_type: normalizeDiaryMemoryType(toOptionalString(value.memory_type)),
    fact_kind: normalizeDiaryMemoryFactKind(value.fact_kind),
    confidence: clampConfidence(toNumber(value.confidence, 0.85)),
    reason,
  };
}

export async function fetchDiaryMemories(options?: {
  limit?: number;
  includeArchived?: boolean;
}): Promise<DiaryMemory[]> {
  let query = supabase
    .from('diary_memories')
    .select('*')
    .order('is_pinned', { ascending: false })
    .order('updated_at', { ascending: false })
    .limit(options?.limit ?? DEFAULT_FETCH_LIMIT);

  if (!options?.includeArchived) {
    query = query.eq('status', 'active');
  }

  const { data, error } = await query;
  if (error) throw toDiaryError(error);

  const rows = (data ?? []) as DiaryMemoryRow[];
  const filteredRows = options?.includeArchived
    ? rows
    : rows.filter((row) => !isDiaryMemoryExpired(row.expires_at));

  return filteredRows.map(mapDiaryMemory);
}

export async function fetchDiaryContext(limit = 12): Promise<DiaryContext> {
  const { data, error } = await supabase.rpc('get_diary_context', {
    p_limit: Math.max(1, Math.min(limit, 30)),
  });

  if (error) throw toDiaryError(error);
  return normalizeDiaryContext(data);
}

export async function requestDiaryMemorySuggestion(
  input: DiaryMemorySuggestionRequest,
): Promise<DiaryMemorySuggestion> {
  const { data, error } = await supabase.functions.invoke('suggest-diary-memory', {
    body: input,
  });

  if (error) throw toDiaryError(error);
  return normalizeDiaryMemorySuggestion(data);
}

export async function createDiaryMemory(draft: DiaryMemoryDraft): Promise<string> {
  const { data, error } = await supabase.rpc('create_diary_memory', {
    p_payload: toDiaryPayload(draft),
  });

  if (error) throw toDiaryError(error);
  if (typeof data !== 'string' || !data) {
    throw new Error('Failed to save diary memory.');
  }

  return data;
}

export async function updateDiaryMemory(
  memoryId: string,
  draft: DiaryMemoryDraft,
): Promise<string> {
  const { data, error } = await supabase.rpc('update_diary_memory', {
    p_payload: {
      id: memoryId,
      ...toDiaryPayloadRecord(draft),
    },
  });

  if (error) throw toDiaryError(error);
  if (typeof data !== 'string' || !data) {
    throw new Error('Failed to update diary memory.');
  }

  return data;
}

export async function deleteDiaryMemory(memoryId: string): Promise<void> {
  const { error } = await supabase.from('diary_memories').delete().eq('id', memoryId);
  if (error) throw toDiaryError(error);
}

export async function toggleDiaryMemoryPin(memoryId: string, isPinned: boolean): Promise<void> {
  const { error } = await supabase
    .from('diary_memories')
    .update({ is_pinned: isPinned })
    .eq('id', memoryId);

  if (error) throw toDiaryError(error);
}

export async function fetchActiveDiaryConstraintLines(userId: string, limit = 6) {
  const { data, error } = await supabase
    .from('diary_memories')
    .select('title, body, memory_type, expires_at')
    .eq('user_id', userId)
    .eq('status', 'active')
    .in('memory_type', ['constraint', 'medical'])
    .order('is_pinned', { ascending: false })
    .order('updated_at', { ascending: false })
    .limit(Math.max(1, Math.min(limit, 12)));

  if (error) throw toDiaryError(error);

  return (
    (data ?? []) as Array<Pick<DiaryMemoryRow, 'title' | 'body' | 'memory_type' | 'expires_at'>>
  )
    .filter((row) => !isDiaryMemoryExpired(row.expires_at))
    .map((row) => {
      const title = row.title?.trim();
      const body = row.body.trim();
      return title && title !== body ? `${title}: ${body}` : body;
    });
}
